import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Field, Input, Table } from "../hr/ui";
import { Spinner } from "./mui";
import { Save } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["inventory", "procurement", "finance", "hr", "operations"];

export default function ManagementSettings() {
  const [s, setS] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = () => {
    api.get("/management/settings").then(setS).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/audit", { params: { limit: 100 } }).then(setAudit).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    try { setS(await api.put("/management/settings", s)); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!s) return <Spinner />;

  const setChain = (i, k, v) => {
    const chain = s.escalation_chain.map((c, j) => j === i ? { ...c, [k]: k === "target" ? v : Number(v) } : c);
    setS({ ...s, escalation_chain: chain });
  };

  return (
    <div className="space-y-5" data-testid="mgmt-settings-page">
      <Card testid="settings-rules-card">
        <SectionHead title="Operational Rules" sub="Drive escalation, repeat detection and reminders"
          right={<Btn variant="primary" onClick={save} data-testid="settings-save"><Save size={15} /> Save</Btn>} />
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Repeat-miss window (days)">
            <Input type="number" value={s.repeat_miss_window_days} data-testid="settings-window-input"
              onChange={(e) => setS({ ...s, repeat_miss_window_days: Number(e.target.value) })} />
          </Field>
          <Field label="Repeat-miss threshold">
            <Input type="number" value={s.repeat_miss_threshold} data-testid="settings-threshold-input"
              onChange={(e) => setS({ ...s, repeat_miss_threshold: Number(e.target.value) })} />
          </Field>
          <Field label="Renewal reminder lead (days)">
            <Input type="number" value={s.renewal_default_lead_days}
              onChange={(e) => setS({ ...s, renewal_default_lead_days: Number(e.target.value) })} />
          </Field>
        </div>

        <div className="mt-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: HC.soft }}>Alert sources</div>
          <div className="flex flex-wrap gap-2">
            {SOURCES.map((src) => {
              const on = s.alert_sources.includes(src);
              return (
                <button key={src} data-testid={`settings-source-${src}`}
                  onClick={() => setS({ ...s, alert_sources: on ? s.alert_sources.filter((x) => x !== src) : [...s.alert_sources, src] })}
                  className="h-9 px-3.5 rounded-full text-[12px] font-semibold border capitalize"
                  style={on ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy }
                    : { background: "#fff", color: HC.text, borderColor: HC.border }}>{src}</button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button data-testid="settings-ai-toggle" onClick={() => setS({ ...s, ai_enabled: !s.ai_enabled })}
            className="h-9 px-3.5 rounded-full text-[12px] font-semibold border"
            style={s.ai_enabled ? { background: HC.green, color: "#fff", borderColor: HC.green }
              : { background: "#fff", color: HC.text, borderColor: HC.border }}>
            AI insights {s.ai_enabled ? "enabled" : "disabled"}
          </button>
          <span className="text-xs" style={{ color: HC.soft }}>When disabled, the deterministic brief is used instead.</span>
        </div>
      </Card>

      <Card testid="settings-chain-card">
        <SectionHead title="Escalation Chain" sub="Who gets notified, and after how long" />
        <div className="space-y-2">
          {s.escalation_chain.map((c, i) => (
            <div key={i} className="grid grid-cols-3 gap-3 items-end rounded-xl border p-3" style={{ borderColor: HC.border }} data-testid={`chain-row-${i}`}>
              <Field label="Level"><Input value={c.level} readOnly /></Field>
              <Field label="Target"><Input value={c.target} onChange={(e) => setChain(i, "target", e.target.value)} data-testid={`chain-target-${i}`} /></Field>
              <Field label="After (minutes)"><Input type="number" value={c.after_minutes} onChange={(e) => setChain(i, "after_minutes", e.target.value)} /></Field>
            </div>
          ))}
        </div>
      </Card>

      <Card testid="settings-audit-card">
        <SectionHead title="Management Audit Trail" sub="Every management action is logged" />
        <Table testid="settings-audit-table" columns={[
          { key: "timestamp", label: "When", render: (r) => fmtDateTime(r.timestamp) },
          { key: "user_name", label: "User" },
          { key: "action", label: "Action" },
          { key: "entity", label: "Entity" },
        ]} rows={audit} rowKey={(r, i) => r.id || i} empty="No management activity yet" />
      </Card>
    </div>
  );
}
